import { graphql } from "gatsby"
import rangeParser from "parse-numeric-range"
import React, { forwardRef, LegacyRef, useState } from "react"
import ReactPlayer, { ReactPlayerProps } from "react-player"
import VisibilitySensor from "react-visibility-sensor"
import { css } from "styled-components"
import VariantContainer from "./variant-container"

/**
 * ReactPlayerProps has an index signature which breaks intellisense when used with Omit so we need to remove it
 * ref: https://stackoverflow.com/a/51956054
 */
type RemoveIndex<T> = {
  [K in keyof T as string extends K
    ? never
    : number extends K
    ? never
    : K]: T[K]
}

type TProgressDetail = {
  key: string
  value: string
}
export interface IVideo extends RemoveIndex<ReactPlayerProps> {
  __typename: "ContentfulVideo"
  aspectRatio?: string
}

export const getProgressIndicator = (data: TProgressDetail) => {
  if (!data) {
    return
  }
  const range = rangeParser(data?.value || "")
  if (Array.isArray(range) && range.length >= 2) {
    return { title: data?.key || "", start: range[0], end: range[1] }
  }
}

const DEFAULT_ASPECT_RATIO = ["16", "9"]
const DEFAULT_ASPECT_RATIO_PROP = `${DEFAULT_ASPECT_RATIO[0]}:${DEFAULT_ASPECT_RATIO[1]}`

const Video = forwardRef(
  (
    {
      aspectRatio = DEFAULT_ASPECT_RATIO_PROP,
      ...rest
    }: Omit<IVideo, "__typename" | "playing" | "onProgress">,
    ref: LegacyRef<ReactPlayer>
  ) => {
    const [playerVisible, setPlayerVisible] = useState(false)
    const ratio = aspectRatio ? aspectRatio.split(":") : DEFAULT_ASPECT_RATIO
    const ratioCalc = `${ratio?.[1] || DEFAULT_ASPECT_RATIO[1]} / ${
      ratio?.[0] || DEFAULT_ASPECT_RATIO[0]
    }`

    return (
      <VisibilitySensor
        minTopValue={200}
        partialVisibility={true}
        onChange={setPlayerVisible}
      >
        <VariantContainer
          variants={[
            css`
              position: relative;
              padding-bottom: calc(${ratioCalc} * 100%);
              height: 0;
            `,
          ]}
        >
          <ReactPlayer
            data-testid="react-player"
            playing={playerVisible}
            width={"100%"}
            height={"100%"}
            style={{ position: "absolute", top: 0, left: 0 }}
            ref={ref}
            {...rest}
          />
        </VariantContainer>
      </VisibilitySensor>
    )
  }
)

export const VideoFragment = graphql`
  fragment ContentfulVideo on ContentfulVideo {
    contentful_id
    url
    muted
    loop
    controls
    volume
    aspectRatio
  }
`
export const VIDEO_TYPENAME = "ContentfulVideo"
export default Video
